// Shared pieces of the Money pages (owner, 2026-10-22: "redesign the tabs according to the website"):
// the views switch styled like Customers and Products, the period switch, summary tiles and the
// header buttons.
import { useState, type ReactNode } from "react";
import { formatDateAD, formatMoney } from "@ady/format";
import { t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";
import { DateRange } from "../orders/OrderFilters";
import { PERIODS, spanRange, type Period, type Span } from "./money";

/** A row of views (segmented, brand tint on the picked one), optionally with a count each. */
export function Views<T extends string>({
  label,
  items,
  value,
  onChange,
  testId,
  compact,
}: {
  label: string;
  items: { id: T; label: string; icon: IconName; count?: number }[];
  value: T;
  onChange: (id: T) => void;
  testId?: string;
  /** Smaller buttons, for a long row of filters. */
  compact?: boolean;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      className="flex max-w-full flex-wrap gap-1 self-start rounded-sm border border-card bg-surface p-1"
    >
      {items.map((v) => {
        const on = v.id === value;
        return (
          <button
            key={v.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(v.id)}
            className={cx(
              compact
                ? "flex min-h-7 items-center gap-1.5 rounded-xs px-2 text-xs font-medium"
                : "flex min-h-control items-center gap-2 rounded-sm px-3 text-sm font-medium",
              on ? "ady-bg-brand-tint" : "text-fg-tertiary hover:bg-hover hover:text-fg",
            )}
          >
            <Icon name={v.icon} gradient={on} />
            <span className={on ? "ady-text-brand" : undefined}>{v.label}</span>
            {v.count !== undefined ? (
              <span className="rounded-full bg-sunken px-1.5 text-2xs font-semibold text-fg-tertiary">
                {v.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The report period as small pills, with the dates it covers. With `onDates` the dates are the
 * Orders calendar: pick any range (Clear goes back to Lifetime).
 */
export function PeriodPills<P extends Span>({
  value,
  onChange,
  only = PERIODS as readonly Period[] as readonly P[],
  custom = { from: "", to: "" },
  onDates,
  align,
}: {
  value: P;
  onChange: (p: P) => void;
  only?: readonly P[];
  custom?: { from: string; to: string };
  onDates?: (range: { from: string; to: string } | null) => void;
  align?: "start" | "end";
}) {
  const [dateOpen, setDateOpen] = useState(false);
  const r = spanRange(value, custom);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="group"
        aria-label={t("payments.money.period.label")}
        className="flex flex-wrap gap-1 rounded-sm border border-card bg-surface p-1"
      >
        {only.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={p === value}
            onClick={() => onChange(p)}
            className={cx(
              "flex min-h-7 items-center rounded-xs px-2.5 text-xs font-medium",
              p === value ? "ady-bg-brand text-on-primary" : "text-fg-secondary hover:bg-hover",
            )}
          >
            {t(`payments.money.period.${p as Span}`)}
          </button>
        ))}
      </div>
      {onDates ? (
        <DateRange
          period={value === "lifetime" ? "all" : "custom"}
          custom={r}
          open={dateOpen}
          onOpenChange={setDateOpen}
          onApply={onDates}
          align={align}
        />
      ) : (
        <span className="flex items-center gap-1 text-xs text-fg-tertiary">
          <Icon name="calendar" />
          {t("payments.finance.range.span", {
            from: formatDateAD(`${r.from}T00:00:00`, "short"),
            to: formatDateAD(`${r.to}T00:00:00`, "short"),
          })}
        </span>
      )}
    </div>
  );
}

/** A summary tile: icon, label, an amount (paisa) or a plain value, and an optional hint. */
export function Tile({
  icon,
  label,
  paisa,
  value,
  hint,
  tone,
  onClick,
  active,
}: {
  icon: IconName;
  label: string;
  paisa?: number;
  value?: ReactNode;
  hint?: ReactNode;
  tone?: "success" | "danger" | "warning";
  onClick?: () => void;
  active?: boolean;
}) {
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-xs font-semibold text-fg-secondary">
        <span
          className={cx(
            "flex size-6 items-center justify-center rounded-xs",
            tone === "success"
              ? "bg-success-bg text-success-fg"
              : tone === "danger"
                ? "bg-danger-bg text-danger-fg"
                : tone === "warning"
                  ? "bg-warning-bg text-warning-fg"
                  : "bg-selected text-link",
          )}
        >
          <Icon name={icon} />
        </span>
        {label}
      </span>
      {paisa !== undefined ? (
        <span className="ady-ledger flex items-baseline gap-1 pt-1.5">
          <span className="text-xs font-medium text-fg-tertiary">{t("today.home.currency")}</span>
          <span className="text-title-sm font-bold">{formatMoney(paisa, { bare: true })}</span>
        </span>
      ) : (
        <span className="ady-ledger pt-1.5 text-title-sm font-bold">{value}</span>
      )}
      {hint ? <span className="pt-0.5 text-2xs text-fg-tertiary">{hint}</span> : null}
    </>
  );
  const cls = cx(
    "flex min-w-0 flex-col rounded-card border bg-surface px-3.5 pt-3 pb-2.5 text-start shadow-card",
    active ? "ady-outline-brand" : "border-card",
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(cls, "hover:bg-hover")}
    >
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/** Header buttons: the first (brand) one is the main action. */
export function HeadButton({
  icon,
  label,
  onClick,
  primary,
  disabled,
  why,
  testId,
}: {
  icon: IconName;
  label: string;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  why?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? why : undefined}
      data-testid={testId}
      className={cx(
        "flex min-h-control items-center gap-1.5 rounded-xs px-3 text-sm font-medium whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-45",
        primary
          ? "ady-bg-brand text-on-primary"
          : "ady-hover-brand border border-card bg-surface text-fg-secondary",
      )}
    >
      <Icon name={icon} />
      <span className={primary ? undefined : "ady-hover-label"}>{label}</span>
    </button>
  );
}

/** The search box used above the Money tables. */
export function SearchBox({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <label className="relative flex min-w-0 flex-1 items-center sm:w-64 sm:flex-none">
      <span className="pointer-events-none absolute start-2.5 text-fg-tertiary">
        <Icon name="search" />
      </span>
      <input
        type="search"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-control w-full rounded-xs border border-control bg-surface ps-8 pe-3 text-sm placeholder:text-fg-tertiary"
      />
    </label>
  );
}
